import Vue from 'vue'
import config from '@/configs'

const FILE_UPLOAD_API = {
  SINGLE_UPLOAD: `${config.API}/api/files/upload/`,
  MULTIPLE_UPLOAD: `${config.API}/api/files/upload-multiple/`,
  DELETE_FILE: `${config.API}/api/files/delete/`,
  // Fallback endpoints
  SIMPLE_UPLOAD: `${config.API}/api/files/simple-upload/`,
  SIMPLE_DELETE: `${config.API}/api/files/simple-delete/`
}

// Helper function to create request headers with token
function createRequestHeaders(token, isMultipart = false) {
  const headers = {
    'Authorization': `Bearer ${token}`,
    'accept': 'application/json'
  }
  
  if (isMultipart) {
    headers['Content-Type'] = 'multipart/form-data'
  } else {
    headers['Content-Type'] = 'application/json'
  }
  
  return headers
}

// Helper function to get token from store or localStorage
function getAuthToken() {
  // Method 1: Try to get from global Vue instance store
  if (typeof window !== 'undefined' && window.getApp && window.getApp.$store) {
    const auth = window.getApp.$store.state.authentication
    if (auth && auth.token) {
      return auth.token
    }
  }
  
  // Method 2: Try to get from Vue prototype store
  if (Vue.prototype.$store && Vue.prototype.$store.state.authentication && Vue.prototype.$store.state.authentication.token) {
    return Vue.prototype.$store.state.authentication.token
  }
  
  // Method 3: Fallback to localStorage
  const authData = localStorage.getItem('authentication')
  if (authData) {
    try {
      const parsed = JSON.parse(authData)
      return parsed.token
    } catch (e) {
      console.warn('Could not parse auth data from localStorage:', e)
    }
  }
  
  // Method 4: Try sessionStorage as fallback
  const sessionAuthData = sessionStorage.getItem('authentication')
  if (sessionAuthData) {
    try {
      const parsed = JSON.parse(sessionAuthData)
      return parsed.token
    } catch (e) {
      console.warn('Could not parse auth data from sessionStorage:', e)
    }
  }
  
  return null
}

class FileUploadService {
  /**
   * Upload single file to backend Firebase proxy
   * @param {File} file - File object to upload
   * @param {string} fileType - Type of file (bill, product, etc.)
   * @param {boolean} useSimple - Use simple local upload instead of Firebase
   * @returns {Promise} Upload response
   */
  async uploadSingleFile(file, fileType = 'bill', useSimple = false) {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('fileType', fileType)
    
    // Get authentication token
    const token = getAuthToken()
    if (!token) {
      return {
        success: false,
        error: 'No authentication token found',
        message: 'Vui lòng đăng nhập để upload file'
      }
    }
    
    try {
      // Choose endpoint based on useSimple flag
      const uploadUrl = useSimple ? FILE_UPLOAD_API.SIMPLE_UPLOAD : FILE_UPLOAD_API.SINGLE_UPLOAD
      
      const response = await Vue.http.post(uploadUrl, formData, {
        headers: createRequestHeaders(token, true),
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          // Có thể emit event hoặc callback để update progress
          console.log(`Upload progress: ${progress}%`)
        }
      })
      
      return {
        success: true,
        data: response.data.data,
        message: 'Upload thành công'
      }
    } catch (error) {
      // Handle authentication errors
      if (error.status === 401) {
        if (window.getApp) {
          window.getApp.$emit('APP_AUTH_FAILED')
        }
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại'
        }
      }
      
      return {
        success: false,
        error: error.response && error.response.data && error.response.data.reason ? error.response.data.reason : 'Lỗi upload file',
        message: error.response && error.response.data && error.response.data.reason ? error.response.data.reason : 'Có lỗi xảy ra khi upload file'
      }
    }
  }
  
  /**
   * Upload multiple files to backend Firebase proxy
   * @param {FileList|Array} files - Files to upload
   * @param {string} fileType - Type of files (bill, product, etc.)
   * @returns {Promise} Upload response
   */
  async uploadMultipleFiles(files, fileType = 'bill') {
    const formData = new FormData()
    
    // Get authentication token
    const token = getAuthToken()
    if (!token) {
      return {
        success: false,
        error: 'No authentication token found',
        message: 'Vui lòng đăng nhập để upload file'
      }
    }
    
    // Add each file to FormData
    for (let i = 0; i < files.length; i++) {
      formData.append(`file_${i}`, files[i])
    }
    formData.append('fileType', fileType)
    
    try {
      const response = await Vue.http.post(FILE_UPLOAD_API.MULTIPLE_UPLOAD, formData, {
        headers: createRequestHeaders(token, true),
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          console.log(`Upload progress: ${progress}%`)
        }
      })
      
      return {
        success: true,
        data: response.data.data,
        message: 'Upload thành công'
      }
    } catch (error) {
      // Handle authentication errors
      if (error.status === 401) {
        if (window.getApp) {
          window.getApp.$emit('APP_AUTH_FAILED')
        }
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại'
        }
      }
      
      return {
        success: false,
        error: error.response && error.response.data && error.response.data.reason ? error.response.data.reason : 'Lỗi upload files',
        message: error.response && error.response.data && error.response.data.reason ? error.response.data.reason : 'Có lỗi xảy ra khi upload files'
      }
    }
  }
  
  /**
   * Delete file from Firebase storage
   * @param {string} filePath - Firebase storage path of file to delete
   * @returns {Promise} Delete response
   */
  async deleteFile(filePath) {
    // Get authentication token
    const token = getAuthToken()
    if (!token) {
      return {
        success: false,
        error: 'No authentication token found',
        message: 'Vui lòng đăng nhập để xóa file'
      }
    }
    
    try {
      const response = await Vue.http.delete(FILE_UPLOAD_API.DELETE_FILE, {
        headers: createRequestHeaders(token),
        body: { filePath }
      })
      
      return {
        success: true,
        data: response.data.data,
        message: 'Xóa file thành công'
      }
    } catch (error) {
      // Handle authentication errors
      if (error.status === 401) {
        if (window.getApp) {
          window.getApp.$emit('APP_AUTH_FAILED')
        }
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại'
        }
      }
      
      return {
        success: false,
        error: error.response && error.response.data && error.response.data.reason ? error.response.data.reason : 'Lỗi xóa file',
        message: error.response && error.response.data && error.response.data.reason ? error.response.data.reason : 'Có lỗi xảy ra khi xóa file'
      }
    }
  }
  
  /**
   * Validate file before upload
   * @param {File} file - File to validate
   * @param {Array} allowedTypes - Allowed file extensions
   * @param {number} maxSize - Maximum file size in MB
   * @returns {Object} Validation result
   */
  validateFile(file, allowedTypes = ['jpg', 'jpeg', 'png', 'gif', 'pdf', 'doc', 'docx', 'xls', 'xlsx'], maxSize = 10) {
    if (!file) {
      return { valid: false, message: 'Không tìm thấy file' }
    }
    
    const fileExtension = file.name.split('.').pop().toLowerCase()
    if (!allowedTypes.includes(fileExtension)) {
      return { 
        valid: false, 
        message: `Định dạng file không được hỗ trợ. Chỉ chấp nhận: ${allowedTypes.join(', ')}` 
      }
    }
    
    const fileSizeMB = file.size / (1024 * 1024)
    if (fileSizeMB > maxSize) {
      return { 
        valid: false, 
        message: `File quá lớn. Kích thước tối đa: ${maxSize}MB` 
      }
    }
    
    return { valid: true, message: 'File hợp lệ' }
  }
  
  /**
   * Generate preview URL for file
   * @param {File} file - File to preview
   * @returns {string} Preview URL
   */
  generatePreviewUrl(file) {
    if (file && file.type.startsWith('image/')) {
      return URL.createObjectURL(file)
    }
    return null
  }
}

export default new FileUploadService()